import React from 'react';
import { Sun, MapPin, Anchor, Download } from 'lucide-react';
import { LOGO } from '../../constants/site';

const LOGO_FILTER = { filter: 'brightness(0) invert(1) sepia(1) saturate(5) hue-rotate(180deg)' };

const RELOCATION_GUIDES = [
  { area: 'hampton_roads', state: 'VIRGINIA', icon: MapPin, title: 'Hampton Roads', headerCls: 'bg-gradient-to-br from-[#143160] to-[#1E4A86]', btnCls: 'bg-gradient-to-r from-[#143160] to-[#1E4A86] hover:from-[#1E4A86] hover:to-[#143160]', label: 'Get Hampton Roads Guide', items: ['Virginia Beach Neighborhoods', 'Norfolk, Chesapeake & Suffolk', 'Military Base Locations (Naval Station Norfolk, JEB Little Creek, Dam Neck)', 'School Districts & Rankings', '2026 Market Predictions'] },
  { area: 'outer_banks', state: 'NORTH CAROLINA', icon: Anchor, title: 'Outer Banks', headerCls: 'bg-gradient-to-br from-[#143160] to-cyan-700', btnCls: 'bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800', label: 'Get Outer Banks Guide', items: ['Corolla, Duck & Southern Shores', 'Kitty Hawk, Kill Devil Hills & Nags Head', 'Coast Guard Base Info (Elizabeth City)', 'Beach Living & Investment Properties', '2026 Market Predictions'] },
];

const CheckIcon = () => (
  <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
);

const RelocationGuideCard = ({ g, onOpen }) => (
  <div className="relative group">
    <div className="absolute -inset-2 bg-gradient-to-r from-gold-400/40 to-orange-400/40 blur-xl rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
    <div className="relative bg-sand-50 rounded-3xl shadow-2xl overflow-hidden transform group-hover:scale-[1.02] transition-all duration-300">
      <div className={`p-6 text-center relative ${g.headerCls}`}>
        <div className="absolute top-3 right-3 bg-gold-400 text-[#143160] px-3 py-1 rounded-full text-xs font-bold">FREE</div>
        <img src={LOGO} alt="Jess Sells East Coast" className="h-14 mx-auto mb-3" loading="lazy" decoding="async" style={LOGO_FILTER} />
        <div className="flex items-center justify-center space-x-2 mb-2">
          <g.icon className="w-5 h-5 text-gold-400" />
          <span className="text-gold-400 font-bold text-sm">{g.state}</span>
        </div>
        <h3 className="text-xl font-serif font-bold text-white mb-1">{g.title}</h3>
        <p className="text-2xl font-serif font-bold text-gold-400">2026 Relocation Guide</p>
      </div>
      <div className="p-5 space-y-2">
        {g.items.map((item) => (
          <div key={item} className="flex items-center space-x-2 text-sm text-gray-700">
            <CheckIcon />
            <span>{item}</span>
          </div>
        ))}
      </div>
      <div className="px-5 pb-5">
        <button data-testid={`open-relocation-${g.area}`} onClick={() => onOpen(g.area)} className={`w-full ${g.btnCls} text-white py-3 rounded-xl font-bold flex items-center justify-center space-x-2 transition-all`}>
          <Download className="w-5 h-5" />
          <span>{g.label}</span>
        </button>
      </div>
    </div>
  </div>
);

export const RelocationGuidesSection = ({ openRelocationGuide }) => (
  <section className="py-20 bg-gradient-to-br from-[#143160] via-[#1E4A86] to-[#143160] relative overflow-hidden">
    <div className="absolute inset-0 opacity-10">
      <div className="absolute top-10 left-10 w-64 h-64 bg-gold-400 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 right-10 w-48 h-48 bg-sand-50 rounded-full blur-2xl"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-cyan-400 rounded-full blur-3xl opacity-30"></div>
    </div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div className="text-center mb-12">
        <div className="inline-flex items-center space-x-2 bg-gold-400 text-[#143160] px-4 py-2 rounded-full font-bold text-sm mb-6">
          <Sun className="w-5 h-5" />
          <span>FREE DOWNLOADS</span>
        </div>
        <h2 className="text-4xl md:text-5xl font-serif font-bold text-white leading-tight mb-4">2026 Relocation<br /><span className="gold-gradient-text">Guides</span></h2>
        <p className="text-white/80 text-lg max-w-2xl mx-auto">Your complete guides to relocating to Coastal Virginia & North Carolina. Choose your destination and get everything you need to know!</p>
      </div>
      <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
        {RELOCATION_GUIDES.map((g) => <RelocationGuideCard key={g.area} g={g} onOpen={openRelocationGuide} />)}
      </div>
      <div className="text-center mt-8">
        <button data-testid="open-relocation-both" onClick={() => openRelocationGuide('both')} className="inline-flex items-center space-x-3 bg-gradient-to-r from-gold-400 to-gold-500 hover:from-gold-500 hover:to-gold-600 text-[#143160] px-8 py-4 rounded-full font-bold text-lg shadow-2xl hover:scale-105 transition-all">
          <Download className="w-6 h-6" />
          <span>Get BOTH Guides FREE</span>
        </button>
      </div>
    </div>
  </section>
);
